import React, { useMemo } from "react";
import {
  X,
  ChevronDown,
  Sparkles,
  Tag,
  Star,
  TrendingUp,
} from "lucide-react";
import "./Filters.css";

const SORT_CHIPS = [
  { key: "new_arrivals", labelAr: "وصل حديثًا", labelEn: "New Arrivals", icon: Sparkles },
  { key: "offers", labelAr: "العروض", labelEn: "Offers", icon: Tag },
  { key: "best_rated", labelAr: "الأعلى تقييماً", labelEn: "Best Rated", icon: Star },
  { key: "best_sellers", labelAr: "الأكثر مبيعًا", labelEn: "Best Sellers", icon: TrendingUp },
];

function getOptionId(option) {
  return String(option?.id ?? option?.value ?? option?.slug ?? option ?? "").trim().toLowerCase();
}

function getOptionLabel(option, isAr) {
  return String(
    isAr
      ? option?.label_ar ?? option?.label_en ?? option?.slug ?? option?.id ?? option ?? ""
      : option?.label_en ?? option?.label_ar ?? option?.slug ?? option?.id ?? option ?? ""
  ).trim();
}

export default function FiltersPanel({
  isAr,
  open,
  onClose,
  panelFilters,
  setPanelFilters,
  panelSort,
  setPanelSort,
  panelRating,
  setPanelRating,
  panelInStock,
  setPanelInStock,
  colors,
  sizes,
  types,
  maxPrice,
  priceChips,
  previewCount,
  onApply,
  onReset,
}) {
  const hasColorOptions = colors.length > 0;
  const hasSizeOptions = sizes.length > 0;
  const hasTypeOptions = types.length > 0;
  const resolveOptionLabel = (options, value) => {
    const selectedId = getOptionId(value);
    const match = options.find((option) => getOptionId(option) === selectedId);
    return match ? getOptionLabel(match, isAr) : value;
  };

  const panelActiveCount = useMemo(() => {
    let count = 0;
    if (panelFilters.color !== "all") count++;
    if (panelFilters.size !== "all") count++;
    if (panelFilters.type !== "all") count++;
    if (panelFilters.price < maxPrice) count++;
    if (panelRating > 0) count++;
    if (panelInStock) count++;
    if (panelSort) count++;
    return count;
  }, [panelFilters, maxPrice, panelRating, panelInStock, panelSort]);

  return (
    <div
      id="filters-panel"
      className={`filters-panel ${open ? "open" : ""}`}
      dir={isAr ? "rtl" : "ltr"}
    >
      <div className="container">
        <div className="filters-content">
          {/* 1. الهيدر */}
          <div className="filters-header">
            <h3>{isAr ? "تصفية المنتجات" : "Filter Products"}</h3>
            <button
              className="close-filters-btn"
              onClick={onClose}
              aria-label={isAr ? "إغلاق الفلاتر" : "Close filters"}
            >
              <X size={16} />
            </button>
          </div>

          {/* 2. الـ Active Filter Tags */}
          {panelActiveCount > 0 && (
            <div className="active-filters">
              {panelSort && (
                <span className="filter-tag">
                  {isAr ? "الترتيب: " : "Sort: "}
                  {SORT_CHIPS.find((c) => c.key === panelSort)?.[isAr ? "labelAr" : "labelEn"]}
                  <button onClick={() => setPanelSort(null)} aria-label={isAr ? "إزالة الترتيب" : "Remove sort"}>
                    <X size={10} />
                  </button>
                </span>
              )}
              {panelFilters.color !== "all" && (
                <span className="filter-tag">
                  {isAr ? "اللون: " : "Color: "} {resolveOptionLabel(colors, panelFilters.color)}
                  <button onClick={() => setPanelFilters((prev) => ({ ...prev, color: "all" }))} aria-label={isAr ? "إزالة اللون" : "Remove color"}>
                    <X size={10} />
                  </button>
                </span>
              )}
              {panelFilters.size !== "all" && (
                <span className="filter-tag">
                  {isAr ? "المقاس: " : "Size: "} {resolveOptionLabel(sizes, panelFilters.size)}
                  <button onClick={() => setPanelFilters((prev) => ({ ...prev, size: "all" }))} aria-label={isAr ? "إزالة المقاس" : "Remove size"}>
                    <X size={10} />
                  </button>
                </span>
              )}
              {panelFilters.type !== "all" && (
                <span className="filter-tag">
                  {isAr ? "النوع: " : "Type: "} {resolveOptionLabel(types, panelFilters.type)}
                  <button onClick={() => setPanelFilters((prev) => ({ ...prev, type: "all" }))} aria-label={isAr ? "إزالة النوع" : "Remove type"}>
                    <X size={10} />
                  </button>
                </span>
              )}
              {panelRating > 0 && (
                <span className="filter-tag">
                  {isAr ? "التقييم: " : "Rating: "} {panelRating}+
                  <button onClick={() => setPanelRating(0)} aria-label={isAr ? "إزالة التقييم" : "Remove rating"}>
                    <X size={10} />
                  </button>
                </span>
              )}
              {panelFilters.price < maxPrice && (
                <span className="filter-tag">
                  {isAr ? "السعر: حتى $" : "Price: Up to $"} {panelFilters.price}
                  <button onClick={() => setPanelFilters((prev) => ({ ...prev, price: maxPrice }))} aria-label={isAr ? "إزالة السعر" : "Remove price"}>
                    <X size={10} />
                  </button>
                </span>
              )}
              {panelInStock && (
                <span className="filter-tag">
                  {isAr ? "متوفر فقط" : "In Stock Only"}
                  <button onClick={() => setPanelInStock(false)} aria-label={isAr ? "إزالة التوفر" : "Remove availability"}>
                    <X size={10} />
                  </button>
                </span>
              )}
            </div>
          )}

          {/* 3. الأزرار — DOM: Reset أولاً، Apply ثانياً */}
          <div className="filters-actions">
             <button className="apply-btn" onClick={onApply}>
              {isAr ? "عرض النتائج" : "Show Results"}
              <span className="btn-count-badge">{previewCount}</span>
            </button>
            <button
              className="reset-btn"
              onClick={onReset}
              disabled={panelActiveCount === 0}
            >
              {isAr ? "إعادة الضبط" : "Reset All"}
            </button>
           
          </div>

          {/* 4. ترتيب حسب */}
          <div className="filters-section">
            <label className="filter-section-label">{isAr ? "ترتيب حسب" : "Sort By"}</label>
            <div className="sort-chips-row">
              {SORT_CHIPS.map((chip) => {
                const Icon = chip.icon;
                const isActive = panelSort === chip.key;
                return (
                  <button
                    key={chip.key}
                    className={`sort-chip-drawer ${isActive ? "active" : ""}`}
                    onClick={() => setPanelSort(isActive ? null : chip.key)}
                  >
                    <Icon size={12} />
                    <span>{isAr ? chip.labelAr : chip.labelEn}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* 5. شبكة الفلاتر */}
          <div className="filters-grid">
            {hasColorOptions && (
              <div className="filter-group">
                <label className="filter-label">{isAr ? "اللون" : "Color"}</label>
                <div className="filter-select-wrapper">
                  <select
                    className="filter-select"
                    value={panelFilters.color}
                    onChange={(e) =>
                      setPanelFilters((prev) => ({ ...prev, color: e.target.value }))
                    }
                  >
                    <option value="all">{isAr ? "كل الألوان" : "All Colors"}</option>
                    {colors.map((option) => (
                      <option key={getOptionId(option)} value={getOptionId(option)}>
                        {getOptionLabel(option, isAr)}
                      </option>
                    ))}
                  </select>
                  <ChevronDown size={12} className="select-icon" />
                </div>
              </div>
            )}

            {hasSizeOptions && (
              <div className="filter-group">
                <label className="filter-label">{isAr ? "المقاس" : "Size"}</label>
                <div className="filter-select-wrapper">
                  <select
                    className="filter-select"
                    value={panelFilters.size}
                    onChange={(e) =>
                      setPanelFilters((prev) => ({ ...prev, size: e.target.value }))
                    }
                  >
                    <option value="all">{isAr ? "كل المقاسات" : "All Sizes"}</option>
                    {sizes.map((option) => (
                      <option key={getOptionId(option)} value={getOptionId(option)}>
                        {getOptionLabel(option, isAr)}
                      </option>
                    ))}
                  </select>
                  <ChevronDown size={12} className="select-icon" />
                </div>
              </div>
            )}

            {hasTypeOptions && (
              <div className="filter-group">
                <label className="filter-label">{isAr ? "النوع" : "Type"}</label>
                <div className="filter-select-wrapper">
                  <select
                    className="filter-select"
                    value={panelFilters.type}
                    onChange={(e) =>
                      setPanelFilters((prev) => ({ ...prev, type: e.target.value }))
                    }
                  >
                    <option value="all">{isAr ? "كل الأنواع" : "All Types"}</option>
                    {types.map((option) => (
                      <option key={getOptionId(option)} value={getOptionId(option)}>
                        {getOptionLabel(option, isAr)}
                      </option>
                    ))}
                  </select>
                  <ChevronDown size={12} className="select-icon" />
                </div>
              </div>
            )}

            {(!hasSizeOptions || !hasTypeOptions) && (
              <div className="filter-data-note">
                {isAr
                  ? "Size and type filters aren’t available for the selected products"
                  : "Size and type filters aren’t available for the selected products"}
              </div>
            )}

            <div className="filter-group rating-filter-group">
              <label className="filter-label">{isAr ? "التقييم" : "Rating"}</label>
              <div className="rating-options">
                {[4, 3, 2, 1].map((stars) => (
                  <button
                    key={stars}
                    className={`rating-chip ${panelRating === stars ? "active" : ""}`}
                    onClick={() => setPanelRating(panelRating === stars ? 0 : stars)}
                  >
                    <span className="rating-stars">{"★".repeat(stars)}</span>
                    <span className="rating-label">{isAr ? "فما فوق" : "& up"}</span>
                  </button>
                ))}
              </div>
            </div>

            <div className="filter-group price-filter-group">
              <label className="filter-label">
                <span>{isAr ? "نطاق السعر" : "Price Range"}</span>
                <span className="price-value">${Number(panelFilters.price || 0).toFixed(0)}</span>
              </label>
              <div className="price-chips">
                {priceChips.map((chip) => (
                  <button
                    key={chip.value}
                    className={`price-chip ${panelFilters.price === chip.value ? "active" : ""}`}
                    onClick={() => setPanelFilters((prev) => ({ ...prev, price: chip.value }))}
                  >
                    {chip.label}
                  </button>
                ))}
              </div>
              <div className="price-slider-wrapper">
                <input
                  type="range"
                  min="0"
                  max={maxPrice || 0}
                  value={panelFilters.price}
                  onChange={(e) =>
                    setPanelFilters((prev) => ({
                      ...prev,
                      price: Number(e.target.value),
                    }))
                  }
                  className="price-slider"
                  aria-label={isAr ? "نطاق السعر" : "Price range"}
                />
                <div className="price-range-labels">
                  <span>$0</span>
                  <span>${maxPrice || 0}</span>
                </div>
              </div>
            </div>

            <div className="filter-group availability-filter-group">
              <label className="filter-label">{isAr ? "التوفر" : "Availability"}</label>
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={panelInStock}
                  onChange={(e) => setPanelInStock(e.target.checked)}
                />
                <span className="checkbox-box" />
                <span className="checkbox-text">{isAr ? "متوفر فقط" : "In Stock Only"}</span>
              </label>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
